import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { analyticsAllowed } from "../lib/cookieConsent";
import { Reveal, useSeo } from "../lib/ui";
import { readSelection, writeSelection } from "../lib/selections";
import { Marquee } from "../components/Marquee";
import { NewsletterBrief } from "../components/NewsletterBrief";
import { SocialScrollRail } from "../components/Social";
import { SpinningMark } from "../components/SpinningMark";
import { Intro } from "../components/Intro";
import { EcosystemFramework } from "../components/Ecosystem";
import { WhatHasMoved } from "../components/WhatHasMoved";
import { BrazilBenchmark } from "../components/BrazilBenchmark";
import { RankedTen } from "../components/RankedTen";

// The journey the whole site describes: nobody stands outside it.
const STAGES = [
  { n: "01", label: "Diaspora and Local Community", to: "/apply", note: "Where the money, the ideas and the people come from" },
  { n: "02", label: "Grassroots", to: "/facilities", note: "Courts, pitches, academies, coaches, referees" },
  { n: "03", label: "Professional Football", to: "/plans", note: "Clubs built to licence, trade and stand up" },
  { n: "04", label: "Harambee Stars", to: "/national-game", note: "A camp base in every county, a squad picked from all of it" },
];

const JourneyStage = ({ stage, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className={`flex-1 ${i ? "-mt-[22px] md:mt-0 md:-ml-[30px]" : ""}`}
  >
    <Link
      to={stage.to}
      data-testid={`journey-stage-${stage.n}`}
      className="seg-chevron group flex h-full flex-col justify-center bg-[#101010] px-10 py-7 transition-colors duration-200 hover:bg-maasai focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E5A91E] md:px-14 md:py-8"
    >
      <span className="mono text-[0.58rem] uppercase tracking-[0.22em] text-ochre transition-colors group-hover:text-white">
        Stage {stage.n}
      </span>
      <span className="mt-2 font-display text-xl font-bold uppercase leading-[0.95] tracking-tight text-white md:text-2xl">
        {stage.label}
      </span>
      <span className="mt-2 hidden text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500 transition-colors group-hover:text-white/80 lg:block">
        {stage.note}
      </span>
    </Link>
  </motion.div>
);

const Hero = ({ settings }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#050505] grain pb-14 pt-16 md:pb-20 md:pt-24" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0" aria-hidden="true">
        <div className="diag-slice absolute inset-x-0 top-0 h-[70%] shuka-check opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute -left-24 top-0 h-[160%] w-px rotate-[18deg] bg-white/10" />
        <div className="absolute left-1/3 top-0 h-[160%] w-px rotate-[18deg] bg-white/[0.06]" />
        <div className="absolute right-16 top-0 h-[160%] w-px rotate-[18deg] bg-maasai/25" />
        <motion.div
          className="absolute left-0 top-1/2 h-px w-full bg-gradient-to-r from-transparent via-maasai to-transparent"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        />
      </motion.div>

      <p
        aria-hidden="true"
        className="text-stroke pointer-events-none absolute -top-2 left-0 w-full select-none whitespace-nowrap font-display text-[18vw] font-bold uppercase leading-none tracking-tighter opacity-[0.14]"
      >
        We all have a role to play
      </p>

      <div className="relative mx-auto w-full max-w-[1500px] px-5 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="eyebrow"
        >
          {settings.hero_kicker || "KickStartKenyaFootball"}
        </motion.p>

        <h1 className="mt-6 max-w-6xl font-display font-bold uppercase tracking-tighter">
          <motion.span
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.24, ease: [0.16, 1, 0.3, 1] }}
            className="block text-2xl leading-none tracking-tight text-zinc-400 md:text-4xl"
          >
            Football History
          </motion.span>
          {[
            { line: "From The Grassroots To The Professionals", tone: "text-white" },
            { line: "From The Harambee Stars To The Diaspora", tone: "text-maasai md:pl-[6%]" },
          ].map((row, i) => (
            <motion.span
              key={row.line}
              initial={{ opacity: 0, y: 44 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.36 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={`mt-2 block text-4xl leading-[0.9] md:text-6xl lg:text-[5rem] ${row.tone}`}
            >
              {row.line}
            </motion.span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.62 }}
          className="mt-9 inline-flex flex-wrap items-center gap-4 border-l-4 border-maasai bg-white/[0.04] py-4 pl-5 pr-7"
          data-testid="hero-strapline"
        >
          <span className="font-display text-2xl font-bold uppercase leading-none tracking-tight text-white md:text-4xl lg:text-5xl">
            We All Have A Role To Play
          </span>
          <span className="mono text-[0.6rem] uppercase tracking-[0.24em] text-ochre">No exceptions</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-9 grid gap-8 md:grid-cols-[1.15fr_1fr] md:gap-16"
        >
          <p className="max-w-2xl text-base leading-relaxed text-zinc-300 md:text-lg" data-testid="hero-proposition">
            <span className="block font-display text-xl font-bold uppercase leading-tight tracking-tight text-white md:text-2xl">
              Kenya has the talent. Now it needs the pathway.
            </span>
            <span className="mt-4 block">
              KickStartKenyaFootball is an independent, evidence-based platform dedicated to preserving
              Kenya&rsquo;s rich football history, mapping opportunities across all 47 counties and mobilising
              private-sector support for a sustainable football industry &mdash; from grassroots and youth
              development to professional clubs and the Harambee Stars.
            </span>
            <span className="mt-4 block border-l-2 border-ochre pl-4 text-sm text-zinc-400">
              If you know ground standing idle in your county, tell us. We publish what a facility there would
              cost to build, what it would cost to run and what it could return.
            </span>
            <Link to="/apply" className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="hero-apply-cta">
              Tell us about ground in your county <ArrowRight size={14} />
            </Link>
          </p>
          <div className="self-end">
            <p className="overline">Kenya&rsquo;s finest, and the plan behind them</p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <Link to="/hall-of-fame/leaderboard" className="btn-primary" data-testid="hero-join-cta">
                Hall of Fame Top 20 <ArrowRight size={14} />
              </Link>
              <Link to="/firsts" className="btn-ghost" data-testid="hero-firsts-cta">
                First To Do It
              </Link>
              <SpinningMark size={104} className="mt-2 md:ml-auto md:mt-0" testId="hero-spinning-mark" />
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

const JourneyBand = () => (
  <section className="border-t border-zinc-900 bg-[#050505] py-14 md:py-20" data-testid="journey-band">
    <div className="mx-auto max-w-[1500px] px-5 md:px-10">
      <Reveal>
        <p className="overline">The journey, stage by stage</p>
      </Reveal>
      <div className="mt-6 flex flex-col md:flex-row md:items-stretch">
        {STAGES.map((stage, i) => (
          <JourneyStage key={stage.n} stage={stage} i={i} />
        ))}
      </div>
    </div>
  </section>
);

// The site's segments: where the game has been, how it gets built, who plays it, what it pays, how to join.
const GROUPS = [
  {
    heading: "Kenya Football History",
    pairs: [
      [
        { n: "01", label: "All Players", to: "/hall-of-fame", note: "The archive of every Kenyan professional, by era" },
        { n: "02", label: "First To Do It", to: "/firsts", note: "Walsall 2001 to the Champions League" },
      ],
    ],
  },
  {
    heading: "The Professional Game and Grassroots",
    pairs: [
      [
        { n: "03", label: "Recommended Steps", to: "/plans", note: "The grassroots, professional or national game, step by step" },
        { n: "04", label: "Grassroots Ecosystem", to: "/facilities", note: "Courts, pitches, academies and what each step builds" },
      ],
      [
        { n: "05", label: "County Map", to: "/counties", note: "All 47 counties, and the corridor of eighteen" },
        { n: "06", label: "Court Rollout Before AFCON", to: "/court-rollout", note: "Courts per county before 19 June 2027, and what they cost" },
      ],
      [
        { n: "07", label: "Training and Jobs", to: "/training-and-jobs", note: "Coaching, refereeing and the roles behind them" },
        { n: "08", label: "Community Impact", to: "/impact", note: "Orphans, women, refugees, disability football" },
      ],
    ],
  },
  {
    heading: "The National Game and Harambee Stars",
    pairs: [
      [
        { n: "09", label: "The National Game", to: "/national-game", note: "One Harambee Stars camp base in every county" },
        { n: "10", label: "National Game Steps", to: "/plans?track=harambee", note: "What a county camp base takes, step by step" },
      ],
      [
        { n: "11", label: "FIFA World Ranking", to: "/fifa-ranking", note: "Every ranking FIFA has published for Kenya since 2000" },
        { n: "12", label: "Ranking Ambition", to: "/ranking-ambition", note: "Where this plan lands Kenya against comparable African nations" },
      ],
      [
        { n: "13", label: "Fixtures and Results", to: "/harambee-fixtures", note: "The next Harambee Stars match, counted down, and the last results" },
        { n: "14", label: "FKF Live Feed", to: "/fkf-feed", note: "Federation news, the league table and the fixtures, daily" },
      ],
    ],
  },
  {
    heading: "Football Financials",
    pairs: [
      [
        { n: "15", label: "Football As An Economy", to: "/football-economy", note: "What the built game gives back to GDP, jobs and identity" },
        { n: "16", label: "The Value Case", to: "/value-case", note: "Facilities, counties and economics reconciled" },
      ],
      [
        { n: "17", label: "The Money", to: "/money", note: "What already moves through Kenyan football" },
        { n: "18", label: "Player Trading Model", to: "/trading", note: "What an academy pathway earns when it sells" },
      ],
      [
        { n: "19", label: "Player Valuations", to: "/valuations", note: "What Kenyan talent is worth today" },
        { n: "20", label: "County Funding Scenarios", to: "/county-scenarios", note: "Model your own selection of counties" },
      ],
      [
        { n: "21", label: "Proposals", to: "/apply", note: "Tell us how you would build it, and what it would cost" },
        { n: "22", label: "Sources and Method", to: "/sources", note: "Every number, who publishes it and when it was last read" },
      ],
    ],
  },
  {
    heading: "The Journey",
    pairs: [
      [
        { n: "23", label: "Football Development Updates", to: "/updates", note: "What has actually moved" },
        { n: "24", label: "News and Stories", to: "/news", note: "Everything we have filed, with the source on it" },
      ],
      [
        { n: "25", label: "Media Enquiries", to: "/contact", note: "Press, advertising and partnerships" },
        { n: "26", label: "About Us", to: "/about", note: "Who is behind this, and why" },
      ],
      [
        { n: "27", label: "Send Us Your Research", to: "/apply", note: "A site, a real cost, a land price or a grassroots project" },
        { n: "28", label: "Terms and Privacy", to: "/terms", note: "How the site works and how your details are handled" },
      ],
    ],
  },
];

const Door = ({ door, second }) => (
  <Link
    to={door.to}
    data-testid={`edition-door-${door.n}`}
    className={`group relative flex min-h-[180px] flex-1 flex-col justify-between px-6 py-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E5A91E] md:min-h-[280px] md:py-14 ${
      second
        ? "border-t border-ochre/40 md:border-l md:border-t-0 md:pl-12 lg:pl-20"
        : "md:pr-12 lg:pr-20"
    } md:px-10 lg:px-16`}
  >
    <span
      aria-hidden="true"
      className="absolute inset-0 origin-bottom scale-y-0 bg-maasai transition-transform duration-200 ease-out group-hover:scale-y-100"
    />
    <span
      aria-hidden="true"
      className="pointer-events-none absolute right-6 top-1 select-none font-display text-[6.5rem] font-bold leading-none text-white/[0.05] transition-colors duration-200 group-hover:text-white/[0.18] md:text-[9rem]"
    >
      {door.n}
    </span>
    <span className="mono relative text-[0.58rem] uppercase tracking-[0.22em] text-zinc-500 transition-colors group-hover:text-white/80">
      {door.n}
    </span>
    <span className="relative">
      <span className="block font-display text-2xl font-bold uppercase leading-[0.94] tracking-tight text-white md:text-4xl">
        {door.label}
      </span>
      <span className="mt-4 flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.14em] text-ochre transition-colors group-hover:text-white">
        {door.note}
        <ArrowUpRight size={13} className="transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    </span>
  </Link>
);

const Step = ({ n, kicker, title, body, links, children, testId }) => (
  <section className="border-t border-zinc-900 bg-black py-16 md:py-24" data-testid={testId}>
    <div className="mx-auto max-w-[1500px] px-5 md:px-10">
      <Reveal>
        <p className="mono text-[0.58rem] uppercase tracking-[0.22em] text-ochre">Step {n} — {kicker}</p>
        <h2 className="display-2 mt-5 max-w-4xl">{title}</h2>
        <p className="body-copy mt-6 max-w-3xl">{body}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          {links.map(([label, to], i) => (
            <Link key={to} to={to} className={i ? "btn-ghost" : "btn-primary"} data-testid={`${testId}-cta-${i}`}>
              {label} {i ? null : <ArrowRight size={14} />}
            </Link>
          ))}
        </div>
      </Reveal>
      {children}
    </div>
  </section>
);

const PlayersStep = () => (
  <Step
    n="01"
    kicker="The players"
    title="Start With What Kenya Has Already Produced"
    body="Every Kenyan professional we can verify, era by era, with what they were worth and where they went. The
      talent is not in question — Kenya has exported it for fifty years. The record is the starting evidence for
      everything that follows."
    links={[["Hall of Fame Top 20", "/hall-of-fame/leaderboard"], ["Player valuations", "/valuations"],
      ["First to do it", "/firsts"], ["The women's game", "/womens-game"]]}
    testId="home-step-players"
  />
);

const FacilitiesStep = () => (
  <Step
    n="02"
    kicker="The facilities"
    title="Build What Brazil Proved Works"
    body="Brazil built a technical base on small-sided volume rather than on wealth, which makes it the fair
      benchmark. Every facility type here is costed at Kenyan prices: what it takes to build, what it takes to
      run and how many players it reaches."
    links={[["Facility library and costs", "/facilities"], ["Facility planner", "/facility-planner"],
      ["Pitch sizing and land", "/pitch-sizing"], ["Modular stadiums", "/modular-stadiums"]]}
    testId="home-step-facilities"
  >
    <BrazilBenchmark />
  </Step>
);

const NumbersStep = () => (
  <Step
    n="04"
    kicker="The numbers"
    title="Capex, Opex And What It Could Return"
    body="What the whole programme costs to build, what it costs to run each year, what it could earn back, and
      the economic and social return alongside it — jobs, tax, health value and players sold. Every figure is a
      research estimate with its source attached, published so it can be argued with."
    links={[["Capex, opex and returns", "/money"], ["The value case", "/value-case"],
      ["County funding scenarios", "/county-scenarios"], ["Player trading model", "/trading"],
      ["Sources and method", "/sources"]]}
    testId="home-step-numbers"
  />
);

const CountyPrinciple = () => (
  <section className="border-t border-zinc-900 bg-black py-16 md:py-24" data-testid="home-county-principle">
    <div className="mx-auto max-w-[1200px] px-5 md:px-10">
      <p className="mono text-[0.58rem] uppercase tracking-[0.22em] text-ochre">Step 03 — the land</p>
      <h2 className="display-2 mt-5 max-w-4xl">Find The Ground, County By County</h2>
      <p className="body-copy mt-6 max-w-3xl">
        Football talent exists across Kenya, but facilities, coaching and competition do not. The map puts all
        47 counties next to their own population, output and land prices, so you can see how much ground a
        county programme needs and what that ground is worth. Eighteen counties are prioritised for the
        professional game; grassroots is all 47. If you can see the site, you can tell us about it.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/counties" className="btn-primary" data-testid="county-principle-map">
          Open the county map <ArrowRight size={14} />
        </Link>
        <Link to="/site-appraisal" className="btn-ghost" data-testid="county-principle-appraisal">
          Appraise a plot
        </Link>
        <Link to="/idle-acre" className="btn-ghost" data-testid="county-principle-idle-acre">
          What an idle acre is worth
        </Link>
        <Link to="/apply" className="btn-ghost" data-testid="county-principle-apply">
          Tell us about ground you know
        </Link>
      </div>
    </div>
  </section>
);

const EditionPairs = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="border-t border-zinc-900 bg-black py-16 md:py-24" data-testid="home-edition">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Explore the complete platform</p>
          <h2 className="display-2 mt-4 max-w-3xl">Every Section, In Full</h2>
          <p className="body-copy mt-5 max-w-2xl">
            Twenty-eight sections, from the archive to the law to the costings. Nothing here has been removed
            &mdash; the four routes above are simply a faster way in.
          </p>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="edition-directory-toggle"
            className="mono mt-7 inline-flex items-center gap-2 border border-zinc-700 px-6 py-3 text-[0.58rem] uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:border-ochre hover:text-ochre"
          >
            {open ? "Hide the full directory" : "Show all 28 sections"}
          </button>
        </Reveal>

        {open ? (
          <div data-testid="edition-directory">
            {GROUPS.map((group, g) => (
              <div key={group.heading} className="mt-16" data-testid={`edition-group-${g + 1}`}>
                <Reveal>
                  <div className="flex items-end gap-5">
                    <h3
                      className="font-display text-xl uppercase tracking-tight text-ochre md:text-2xl"
                      data-testid={`edition-group-heading-${g + 1}`}
                    >
                      {group.heading}
                    </h3>
                    <span aria-hidden="true" className="mb-2 h-px flex-1 bg-white/20" />
                    <span className="mono mb-1 text-[0.58rem] uppercase tracking-[0.2em] text-zinc-600">
                      {String(group.pairs.length * 2).padStart(2, "0")}
                    </span>
                  </div>
                </Reveal>
                <div className="-mx-5 mt-7 space-y-8 md:-mx-10 md:space-y-10">
                  {group.pairs.map((pair, i) => (
                    <Reveal key={pair[0].n} delay={Math.min(i * 0.04, 0.2)}>
                      <div className="seg-band bg-ochre/45 p-[1.5px]">
                        <div
                          className="seg-band relative flex flex-col overflow-hidden bg-[#0a0a0a] md:flex-row md:items-stretch"
                          data-testid={`edition-pair-${pair[0].n}`}
                        >
                          {pair.map((door, j) => (
                            <Door key={door.n} door={door} second={j > 0} />
                          ))}
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default function Home() {
  const [data, setData] = useState({ settings: {} });
  const [loadError, setLoadError] = useState(false);
  // The landing sequence plays once per browser session.
  const [intro, setIntro] = useState(() => !readSelection("kskf-intro-seen", false));

  useSeo({
    title: "KickStartKenyaFootball — Our Youth. Our Plan. Our Football.",
    description:
      "Kenya has the talent. Now it needs the pathway. An independent, evidence-based platform preserving Kenya's football history, mapping opportunities across all 47 counties and mobilising private-sector support for a sustainable football industry.",
  });

  useEffect(() => {
    const load = async () => {
      const settings = await api.get("/settings");
      setData({ settings: settings.data });
    };
    load().catch(() => setLoadError(true));
    if (analyticsAllowed()) api.post("/track", { kind: "page", ref: "/" }).catch(() => {});
  }, []);

  if (loadError)
    return (
      <div className="flex min-h-[70svh] items-center justify-center px-5 text-center" data-testid="home-load-error">
        <div>
          <h1 className="display-3">We couldn&rsquo;t load the site content</h1>
          <p className="body-copy mt-4">Check your connection and try again.</p>
          <button onClick={() => window.location.reload()} className="btn-primary mt-8">
            Retry
          </button>
        </div>
      </div>
    );

  return (
    <div data-testid="home-page">
      {intro ? (
        <Intro
          onDone={() => {
            writeSelection("kskf-intro-seen", true);
            setIntro(false);
          }}
        />
      ) : null}
      {/* The hero: parallax slice, staggered headline, and the turning mark. */}
      <Hero settings={data.settings} />
      {/* The strapline, running edge to edge. */}
      <Marquee text="We all have a role to play." />
      {/* The research, in the order it reads: players, facilities, land, numbers. */}
      <PlayersStep />
      <FacilitiesStep />
      <CountyPrinciple />
      <NumbersStep />
      <RankedTen />
      {/* Dated proof that something is actually happening. */}
      <WhatHasMoved />
      {/* The fifteen parts of a football industry, from the shared module. */}
      <EcosystemFramework />
      {/* Grassroots to diaspora, as one road. */}
      <JourneyBand />
      {/* The full directory of every section, collapsed by default. */}
      <EditionPairs />
      {/* Email capture for the weekly brief. */}
      <NewsletterBrief />
      {/* Socials, revealed once the reader is past the hero. */}
      <SocialScrollRail />
    </div>
  );
}
